import { useMemo } from 'react';
import { Modal } from '@/components/UI';
import DataTable from '@/components/UI/DataTable';
import { trpc } from '@/api/trpc';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { ScreenerOptimizerResultFoldsRow } from '@/types/appRouter';
import dayjs from 'dayjs';
import {
    computeNegAvoidedFromFold,
    computePosCapturedFromFold,
    formatDecimal,
    formatPct01,
    formatPct01Number,
    profitColorClass,
    toNumber
} from './helpers';

interface FoldsModalProps {
    resultId: number | null;
    onClose: () => void;
}

const FoldsModal = ({ resultId, onClose }: FoldsModalProps) => {
    const foldsQuery = useQuery({
        ...trpc.screener.optimizer.resultFolds.queryOptions({ resultId: resultId ?? 0 }),
        enabled: resultId !== null
    });

    const foldRows = foldsQuery.data?.folds ?? [];

    const foldColumns = useMemo((): ColumnDef<ScreenerOptimizerResultFoldsRow>[] => {
        return [
            {
                header: 'Kind',
                accessorKey: 'foldKind',
                size: 90
            },
            {
                header: '#',
                accessorKey: 'foldIndex',
                size: 60
            },
            {
                header: 'Range',
                id: 'range',
                size: 220,
                cell: ({ row }) => (
                    <span className="text-zinc-300">
                        {dayjs.unix(row.original.startTimestamp).format('MMM D, YYYY')} →{' '}
                        {dayjs.unix(row.original.endTimestamp).format('MMM D, YYYY')}
                    </span>
                )
            },
            {
                header: 'Coverage',
                accessorKey: 'coverage',
                size: 110,
                cell: ({ row }) => <span className="text-white">{formatPct01(row.original.coverage, 1)}</span>
            },
            {
                header: 'PosCaptured',
                id: 'posCaptured',
                size: 130,
                cell: ({ row }) => <span className="text-white">{formatPct01Number(computePosCapturedFromFold(row.original), 1)}</span>
            },
            {
                header: 'NegAvoided',
                id: 'negAvoided',
                size: 130,
                cell: ({ row }) => <span className="text-white">{formatPct01Number(computeNegAvoidedFromFold(row.original), 1)}</span>
            },
            {
                header: 'Baseline',
                accessorKey: 'baselineMeanProfit',
                size: 110,
                cell: ({ row }) => <span className="text-white">{formatDecimal(row.original.baselineMeanProfit, 2)}%</span>
            },
            {
                header: 'Rule',
                accessorKey: 'ruleMeanProfit',
                size: 100,
                cell: ({ row }) => (
                    <span className={profitColorClass(row.original.ruleMeanProfit)}>{formatDecimal(row.original.ruleMeanProfit, 2)}%</span>
                )
            },
            {
                header: 'Lift',
                accessorKey: 'lift',
                size: 100,
                cell: ({ row }) => {
                    const value = toNumber(row.original.lift);
                    const color = value >= 0 ? 'text-green-400' : 'text-red-400';
                    return <span className={color}>{formatDecimal(row.original.lift, 2)}%</span>;
                }
            },
            {
                header: 'Entities',
                accessorKey: 'uniqueEntities',
                size: 90,
                cell: ({ row }) => <span className="text-white">{row.original.uniqueEntities}</span>
            }
        ];
    }, []);

    return (
        <Modal isOpen={resultId !== null} size="lg" title="Result Folds" onClose={onClose}>
            {resultId && <div className="text-zinc-400 text-sm mb-3">resultId={resultId}</div>}

            {foldsQuery.isError && (
                <div className="text-red-300 text-sm mb-2">Failed to load folds: {foldsQuery.error?.message ?? 'Unknown error'}</div>
            )}
            {foldsQuery.isFetching && foldRows.length === 0 ? (
                <div className="text-zinc-400 text-sm">Loading folds…</div>
            ) : foldRows.length === 0 && !foldsQuery.isError ? (
                <div className="text-zinc-400 text-sm">No fold metrics yet.</div>
            ) : foldRows.length > 0 ? (
                <DataTable columns={foldColumns} data={foldRows} pagination={{ enabled: false, pageSize: 30 }} search={false} />
            ) : null}
        </Modal>
    );
};

export default FoldsModal;
